import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BookingService } from 'src/app/Services/booking.service';
import { CustomToasterServiceService } from 'src/app/Services/custom-toaster-service.service';
import { ConfirmationServiceService } from 'src/app/Services/confirmation-service.service';

interface CancellationReason {
  cancellationReasonId: number;
  reasonText: string;
  reasonFor?: string;
}

@Component({
  selector: 'app-cancellation-reasons',
  templateUrl: './cancellation-reasons.component.html',
  styleUrls: ['./cancellation-reasons.component.css']
})
export class CancellationReasonsComponent implements OnInit {
  activeTab: 'Admin' | 'Tourist' = 'Admin';
  adminReasons: CancellationReason[] = [];
  touristReasons: CancellationReason[] = [];
  isLoading = false;

  reasonForm: FormGroup;
  showModal = false;
  isEditing = false;
  currentReasonId = 0;

  constructor(
    private fb: FormBuilder,
    private bookingService: BookingService,
    private toaster: CustomToasterServiceService,
    private confirmService: ConfirmationServiceService
  ) {
    this.reasonForm = this.fb.group({
      reasonText: ['', Validators.required],
      reasonFor: ['Admin', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadReasons();
  }

  setTab(tab: 'Admin' | 'Tourist') {
    this.activeTab = tab;
  }

  loadReasons() {
    this.isLoading = true;
    this.loadAdminReasons();
    this.loadTouristReasons();
  }

  loadAdminReasons() {
    this.bookingService.getAdminCancellationReasons().subscribe({
      next: (res: any) => {
        this.adminReasons = Array.isArray(res) ? res : (res?.items || []);
        this.checkLoading();
      },
      error: () => this.checkLoading()
    });
  }

  loadTouristReasons() {
    this.bookingService.getTouristCancellationReasons().subscribe({
      next: (res: any) => {
        this.touristReasons = Array.isArray(res) ? res : (res?.items || []);
        this.checkLoading();
      },
      error: () => this.checkLoading()
    });
  }

  private loadCounter = 0;
  checkLoading() {
    this.loadCounter++;
    if (this.loadCounter >= 2) {
      this.isLoading = false;
      this.loadCounter = 0;
    }
  }

  openModal(reason?: CancellationReason) {
    if (reason) {
      this.isEditing = true;
      this.currentReasonId = reason.cancellationReasonId;
      this.reasonForm.patchValue({
        reasonText: reason.reasonText,
        reasonFor: this.activeTab
      });
    } else {
      this.isEditing = false;
      this.currentReasonId = 0;
      this.reasonForm.reset();
      this.reasonForm.patchValue({ reasonFor: this.activeTab });
    }
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.reasonForm.reset();
  }

  submitReason() {
    if (this.reasonForm.invalid) return;

    this.toaster.process(this.isEditing ? 'Updating reason...' : 'Adding reason...');
    
    const formValue = this.reasonForm.getRawValue();

    const payload = {
      cancellationReasonId: this.currentReasonId,
      reasonText: formValue.reasonText,
      reasonFor: formValue.reasonFor
    };

    this.bookingService.upsertCancellationReason(payload).subscribe({
      next: () => {
        this.toaster.success(this.isEditing ? 'Reason updated successfully' : 'Reason added successfully');
        this.closeModal();
        this.loadReasons();
      },
      error: () => {
        this.toaster.error('Failed to save reason');
      }
    });
  }

  deleteReason(reason: CancellationReason) {
    this.confirmService.openConfirmDialog('Are you sure you want to delete this reason?', () => {
      this.toaster.process('Deleting...');
      this.bookingService.deleteCancellationReason(reason.cancellationReasonId).subscribe({
        next: () => {
          this.toaster.success('Reason deleted successfully');
          this.loadReasons();
        },
        error: () => {
          this.toaster.error('Failed to delete reason');
        }
      });
    });
  }
}
